import { SafeError, SafePromise } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { UserRestful } from './restful';
import { Session } from '@benzinga/session';

interface ErrorEvent {
  error: SafeError;
  errorType: 'get_settings_error' | 'set_setting_error';
  type: 'error';
}

export interface FetchedUserSettingsEvent {
  settings: Record<string, unknown>;
  type: 'user_settings:fetched';
}
export interface SetUserSettingsEvent {
  type: 'user_settings:set';
  key: string;
  value: unknown;
}

export type UserRestfulEvent = ErrorEvent | FetchedUserSettingsEvent | SetUserSettingsEvent;

export class UserRequest extends Subscribable<UserRestfulEvent> {
  private restful: UserRestful;
  private activeRequests: Map<string, NodeJS.Timeout> = new Map();
  private pendingByKey: Map<string, object> = new Map();
  private request_timer = 1000;

  constructor(session: Session) {
    super();
    this.restful = new UserRestful(session);
  }

  public getUserSettings = async (): SafePromise<Record<string, object>> => {
    const res = await this.restful.getUserSettings();
    if (res.err) {
      this.dispatch({
        error: res.err,
        errorType: 'get_settings_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        settings: res.ok,
        type: 'user_settings:fetched',
      });
    }
    return res;
  };

  public setUserSettings = (key: string, value: object) => {
    this.pendingByKey.set(key, value);
    const activeRequest = this.activeRequests.get(key);
    if (activeRequest) {
      clearTimeout(activeRequest);
    }
    this.activeRequests.set(
      key,
      setTimeout(() => {
        this.activeRequests.delete(key);
        this.sendPendingSetting(key);
      }, this.request_timer),
    );
  };

  public flushUserSettings = () => {
    this.activeRequests.forEach(timer => clearTimeout(timer));
    this.activeRequests.clear();
    [...this.pendingByKey.keys()].forEach(key => this.sendPendingSetting(key));
  };

  private sendPendingSetting = (key: string) => {
    const value = this.pendingByKey.get(key);
    if (!value) {
      return;
    }
    this.pendingByKey.delete(key);
    this.restful.setUserSetting(key, value).then(res => {
      if (res.err) {
        this.dispatch({
          error: res.err,
          errorType: 'set_setting_error',
          type: 'error',
        });
      } else {
        this.dispatch({
          key,
          type: 'user_settings:set',
          value,
        });
      }
    });
  };
}
